import {FC, useState, useEffect} from 'react'
import {showSwal} from '../../../../ui-kit'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button, Spinner, Form} from 'react-bootstrap'
import {getEconomyGroupById, createEconomyGroup, updateEconomyGroup} from './core/_requests'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams, useLocation} from 'react-router-dom'
import {EconomyGroup} from './core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'



const EconomyGroupsDetailPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const [internalLoading, setInternalLoading] = useState(false)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {id} = useParams()
  const isDetailMode = Boolean(id)
  const isAddMode = !id
  const [isEditMode, setIsEditMode] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  const [detail, setDetail] = useState<EconomyGroup>({
    id: 0,
    name: '',
    category: '',
  })

  const fetchDetail = async () => {
    if (!id) return
    try {
      setInternalLoading(true)
      const res = await getEconomyGroupById(axiosPrivate, id)
      if (res.data && res.data.data) {
        setDetail(res.data.data)
      }
    } catch (error) {
      console.error(error)
      showSwal('Error', 'Failed to fetch details', 'error')
    } finally {
      setInternalLoading(false)
    }
  }

  useEffect(() => {
    if (isDetailMode) {
      fetchDetail()
    }
  }, [id])

  const location = useLocation()
  useEffect(() => {
    if (location.pathname.endsWith('/edit')) {
      setIsEditMode(true)
    } else {
      setIsEditMode(false)
    }
  }, [location.pathname, id])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  useEffect(() => {
    setIsFormValid(detail.name.trim() !== '' && detail.category.trim() !== '')
  }, [detail.name, detail.category])

  const handleSubmit = () => {
    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this group?`,
      icon: 'question',
    }).then(
      (result) => {
        if (!result.isConfirmed) return

        setInternalLoading(true)

        const payload = {
          name: detail.name,
          category: detail.category
        }

        const action = isEditMode
          ? updateEconomyGroup(axiosPrivate, id, payload)
          : createEconomyGroup(axiosPrivate, payload)

        action
          .then((res) => {
            if (res.status === 200) {
              showSwal(`Data ${isEditMode ? 'updated' : 'created'}`, undefined, 'success').then(() => {
                if (isEditMode) {
                  window.location.reload()
                } else {
                  navigate('/economy-groups')
                }
              })
            }
          })
          .catch((err) => {
            console.error(err)
            const errorMsg =
              err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} data`
            showSwal('Error', errorMsg, 'error')
          })
          .finally(() => {
            setInternalLoading(false)
          })
      }
    )
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/economy-groups')
        } else {
          setIsEditMode(false)
        }
      }
    })
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
          {title: 'Economy Groups', path: 'economy-groups', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {isAddMode ? 'Add Group' : isEditMode ? 'Edit Group' : detail.name || 'Detail'}
      </PageTitle>
      <div>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-globe px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode
                          ? 'Add New Group'
                          : isEditMode
                          ? `Edit ${detail.name}`
                          : `Detail ${detail.name}`}
                      </h3>
                    </div>
                    <div className='d-flex gap-2'>
                      {ability.can('update', subject) && isDetailMode && !isEditMode ? (
                        <Button
                          className='btn btn-primary d-flex gap-4 align-items-center'
                          onClick={() => setIsEditMode(true)}
                          disabled={internalLoading}
                        >
                          <span className='fa fa-pencil'></span>
                          Edit
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!internalLoading ? (
                  <>
                    {isAddMode || isEditMode ? (
                      <Form>
                        <h3 className='mb-5'>General Information</h3>
                        <Row>
                          <Col md={6} className='mb-3'>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>Name</Form.Label>
                              <Form.Control
                                type='text'
                                name='name'
                                value={detail.name}
                                onChange={handleChange}
                                placeholder='Enter group name'
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className='text-primary me-0 ps-0'>Category</Form.Label>
                              <Form.Control
                                type='text'
                                name='category'
                                value={detail.category || ''}
                                onChange={handleChange}
                                placeholder='Enter category (e.g., Region, Income Level)'
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className='row mt-10'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={!isFormValid}
                            >
                              Submit
                            </Button>
                          </div>
                        </div>
                      </Form>
                    ) : (
                      <div>
                        <h3>General Information</h3>
                        <div className='row mb-4'>
                          <div className='col-md-6'>
                            <Form.Label>Name</Form.Label>
                            <div className='data-value'>{detail.name}</div>
                          </div>
                          <div className='col-md-6'>
                            <Form.Label>Category</Form.Label>
                            <div className='data-value'>{detail.category || '-'}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '200px'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default EconomyGroupsDetailPage
